import { Check, SkipForward } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SETUP_STAGE_LABELS, type SetupStageView } from '@/lib/company-setup';
import type { SetupStage } from '@shared/types/company-qualification';

interface Props {
  stages: SetupStageView[];
  active: SetupStage;
  onSelect: (stage: SetupStage) => void;
}

const STATUS_LABELS: Record<SetupStageView['status'], string> = {
  done: 'Fait',
  skipped: 'Passée',
  started: 'En cours',
  todo: 'À faire',
};

/**
 * The stages of the setup as a pipeline: where each one stands, how far the
 * whole goes. Every stage opens on a click, whatever the others' state.
 */
export const SetupPipeline = ({ stages, active, onSelect }: Props) => {
  const finished = stages.filter((view) => view.status === 'done' || view.status === 'skipped').length;
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <div className="h-2 flex-1 overflow-hidden rounded-full bg-domino-muted" role="progressbar" aria-valuemin={0} aria-valuemax={stages.length} aria-valuenow={finished} aria-label="Avancement de la mise en place">
          <div className="h-full w-full origin-left bg-domino-aubergine transition-transform duration-300 ease-snap" style={{ transform: `scaleX(${stages.length ? finished / stages.length : 0})` }} />
        </div>
        <span className="shrink-0 text-sm text-domino-inkSoft">{finished} étape{finished > 1 ? 's' : ''} sur {stages.length}</span>
      </div>
      <ol className="grid grid-cols-[repeat(auto-fit,minmax(9.5rem,1fr))] gap-2" aria-label="Étapes de la mise en place">
        {stages.map((view, index) => (
          <li key={view.stage}>
            <button
              type="button"
              aria-current={active === view.stage ? 'step' : undefined}
              onClick={() => onSelect(view.stage)}
              className={cn(
                'flex h-full w-full flex-col gap-1 rounded-xl border px-3 py-2.5 text-left transition-colors',
                active === view.stage ? 'border-domino-aubergine bg-domino-lilac/70' : 'border-domino-cardBorder bg-domino-warm hover:bg-white',
                view.status === 'skipped' && active !== view.stage && 'opacity-60',
              )}
            >
              <span className="flex items-center justify-between gap-2 text-xs text-domino-inkSoft">
                <span>Étape {index + 1}</span>
                {view.status === 'done' && <Check className="h-4 w-4 text-emerald-700" aria-hidden />}
                {view.status === 'skipped' && <SkipForward className="h-4 w-4" aria-hidden />}
              </span>
              <span className="text-sm font-semibold leading-5 text-domino-ink">{SETUP_STAGE_LABELS[view.stage]}</span>
              <span className={cn('text-xs', view.status === 'done' ? 'text-emerald-700' : 'text-domino-inkSoft')}>
                {STATUS_LABELS[view.status]}{view.openCount > 0 && view.status !== 'skipped' ? ` · ${view.doneCount}/${view.openCount}` : ''}
              </span>
            </button>
          </li>
        ))}
      </ol>
    </div>
  );
};
